import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Plus, X } from "lucide-react";
import { suggestSkills, canonicalizeSkill } from "../lib/skills";
import { toast } from "sonner";

/**
 * Multi-select input backed by the canonical skills catalog.
 *
 * - Requires >=3 chars before suggestions appear.
 * - Only accepts values from the canonical list (case-insensitive).
 * - Enter picks the highlighted suggestion; Tab / click also works.
 * - Existing values are rendered as removable chips.
 *
 * Props:
 * - values: string[] currently selected
 * - onChange: (next: string[]) => void
 * - label: field label
 * - placeholder: input placeholder
 * - testidPrefix: root for data-testid
 */
export default function SkillsAutocomplete({
  values = [],
  onChange,
  placeholder = "Type at least 3 characters…",
  testidPrefix = "skill",
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const [openIdx, setOpenIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const suggestions = useMemo(() => suggestSkills(q, values), [q, values]);

  useEffect(() => {
    setOpenIdx(0);
  }, [q]);

  // Close suggestion popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const commit = (skill) => {
    const canonical = canonicalizeSkill(skill);
    if (!canonical) {
      toast.error("Please pick from the suggestions — we only accept known QA skills.");
      return;
    }
    if (values.some((s) => s.toLowerCase() === canonical.toLowerCase())) {
      toast.info("Already added");
      setQ("");
      return;
    }
    onChange?.([...values, canonical]);
    setQ("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        const c = canonicalizeSkill(q);
        if (c) commit(c);
        else if (q.trim()) toast.error("Pick a skill from the suggestions.");
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpenIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpenIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      commit(suggestions[openIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const remove = (skill) => {
    onChange?.(values.filter((s) => s !== skill));
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={`${testidPrefix}-wrapper`}>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-chip-list`}>
        {values.map((s) => (
          <span
            key={s}
            data-testid={`${testidPrefix}-chip-${s}`}
            className="tag-mono inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded text-xs"
          >
            {s}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(s)}
                aria-label={`Remove ${s}`}
                data-testid={`${testidPrefix}-remove-${s}`}
                className="text-blue-600 hover:text-blue-900"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`${testidPrefix}-input`}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testidPrefix}-suggestions`}
          role="listbox"
        >
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={s}
              role="option"
              aria-selected={i === openIdx}
              onMouseEnter={() => setOpenIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); commit(s); }}
              data-testid={`${testidPrefix}-suggestion-${s}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                i === openIdx ? "bg-blue-50 text-[#002FA7]" : "text-zinc-800 hover:bg-zinc-50"
              }`}
            >
              <Plus className="h-3.5 w-3.5" /> {s}
            </button>
          ))}
        </div>
      )}
      {open && q.trim().length > 0 && q.trim().length < 3 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 px-3 py-2 text-xs text-zinc-500 tag-mono bg-white border border-zinc-200 rounded-md">
          Type at least 3 characters…
        </div>
      )}
      {open && q.trim().length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 px-3 py-2 text-xs text-amber-800 tag-mono bg-amber-50 border border-amber-200 rounded-md">
          No matches — only skills in our accepted catalog can be added.
        </div>
      )}
    </div>
  );
}
